// The words a custom field filter shows: condition pill names, the tile's count, the field type in the drawer
// heading, and one row per applied value. The panel's applied-filter chips reuse the row text, without the
// Not the drawer puts before it, because their group header already shows the Exclude badge. Dropdown rows
// show option names, never ids.
import {
	formatCustomFieldValue,
	type CustomFieldColumnDef,
	type CustomFieldKind,
} from '../custom-field-columns/customFieldColumnModel';
import {EMPTY_OPTION, isValidCondition} from './customFieldFilterConditions';
import type {ConditionOp} from './customFieldFilterInput';

export type FormatMessage = (descriptor: {id: string}, values?: Record<string, string | number>) => string;

export const CONDITION_MESSAGE_IDS: Record<ConditionOp, string> = {
	contains: 'filters.custom_field.contains',
	is: 'filters.custom_field.is_exactly',
	empty: 'filters.custom_field.is_empty',
	equals: 'filters.custom_field.equals',
	gt: 'filters.custom_field.greater_than',
	lt: 'filters.custom_field.less_than',
	between: 'filters.custom_field.between',
	today: 'common.today',
	this_week: 'filter_type.deadline.this_week_option',
	this_month: 'filter_type.deadline.this_month',
	last_30_days: 'date_span_picker.last_30_days',
	next_30_days: 'date_span_picker.next_30_days',
	custom_range: 'common.custom_range',
};

export const RANGE_INVALID_MESSAGE_ID = 'filters.custom_field.range_invalid';

const TYPE_MESSAGE_IDS: Record<CustomFieldKind, string> = {
	TEXT: 'filters.custom_field.type.text',
	DROPDOWN: 'filters.custom_field.type.dropdown',
	INTEGER: 'filters.custom_field.type.integer',
	DECIMAL: 'filters.custom_field.type.decimal',
	DATE: 'filters.custom_field.type.date',
};

const ROW_MESSAGE_IDS = {
	contains: 'filters.custom_field.row.contains',
	is: 'filters.custom_field.row.is_exactly',
	empty: 'filters.custom_field.row.is_empty',
	equals: 'filters.custom_field.row.equals',
	gt: 'filters.custom_field.row.greater_than',
	lt: 'filters.custom_field.row.less_than',
	between: 'filters.custom_field.row.between',
	dateRange: 'filters.custom_field.row.date_range',
};

export interface CustomFieldFilterFormat {
	locale: string;
	decimalSeparator: ',' | '.';
}

/** One applied value as a row or chip reads it: "Contains "acme"", "Between 10 and 20", "This week", "Gold". */
export function customFieldValueLabel(
	def: CustomFieldColumnDef,
	id: unknown,
	formatMessage: FormatMessage,
	format: CustomFieldFilterFormat
): string {
	if (def.kind === 'DROPDOWN') {
		if (id === EMPTY_OPTION) return formatMessage({id: ROW_MESSAGE_IDS.empty});
		return def.options.find(option => option.id === id)?.displayName ?? '';
	}
	if (!isValidCondition(def, id)) return '';
	const show = (value: string): string => formatCustomFieldValue(def, value, format.locale, format.decimalSeparator);
	switch (id.op) {
		case 'empty':
			return formatMessage({id: ROW_MESSAGE_IDS.empty});
		case 'preset':
			return formatMessage({id: CONDITION_MESSAGE_IDS[id.preset]});
		case 'between':
			return formatMessage(
				{id: def.kind === 'DATE' ? ROW_MESSAGE_IDS.dateRange : ROW_MESSAGE_IDS.between},
				{from: show(id.from), to: show(id.to)}
			);
		default:
			return formatMessage({id: ROW_MESSAGE_IDS[id.op]}, {value: def.kind === 'TEXT' ? id.value : show(id.value)});
	}
}

/** The word before each drawer row of an excluded field. */
export const NOT_MESSAGE_ID = 'filters.custom_field.not';

/** A tile's count: nothing until a value is applied, then "(3)", or "Not (3)" when excluded. */
export function customFieldCountLabel(count: number, excluded: boolean, formatMessage: FormatMessage): string {
	if (count === 0) return '';
	return formatMessage({id: excluded ? 'filters.custom_field.count_not' : 'filters.custom_field.count'}, {count});
}

/** The field type as the drawer heading shows it after the field's name: "(Dropdown)". */
export function customFieldTypeLabel(def: CustomFieldColumnDef, formatMessage: FormatMessage): string {
	return formatMessage({id: 'filters.custom_field.type_suffix'}, {type: formatMessage({id: TYPE_MESSAGE_IDS[def.kind]})});
}
